//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { Asserts } from 'yup'
import {
  yupEthAddr,
  LoanLifeCycleState,
  LoanStateToString,
  LoanTransitionStateToString,
  yupApr,
  yupErc20Wei,
} from './Enums'
import { EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

import { APR, ERC20Wei } from './conversions'

export const LoanCTOSchema = yup
  .object({
    id: yup
      .string()
      .label('LoanID loanAddr')
      .required()
      .meta(toFieldMetaData({ propName: 'id', description: 'ID of Loan ', stringType: StringTypeEnum.ID })),

    loanAddr: yupEthAddr.label('Loan Address').meta(
      toFieldMetaData({
        propName: 'loanAddr',
        description:
          ' The Ethereum contract address for the created/requested/active/matured loan. Will be 0x...00000 for loans in Requested State. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
    borrowerAddr: yupEthAddr
      .nullable()
      .label('Borrower Address')
      .meta(
        toFieldMetaData({
          propName: 'borrowerAddr',
          description:
            ' The Ethereum wallet address for the Borrower the created/requested/active/matured loan. Will 0x...00000 for loans in Requested State. ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    fundingVaultAddr: yupEthAddr
      .required()
      .label('Funding Vault')
      .meta(
        toFieldMetaData({
          propName: 'fundingVaultAddr',
          description:
            ' The Ethereum contract address for the Funding Vault of the created/requested/active/matured loan Will be 0x...00000 for loans in Requested State ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    poolAddr: yupEthAddr
      .required()
      .label('Pool Address')
      .meta(
        toFieldMetaData({
          propName: 'poolAddr',
          description: ' TODO ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),

    state: yup
      .number()
      .required()
      .default(0)
      .max(7)
      .min(0)
      .label('Loan Status')
      .meta(toFieldMetaData({ propName: 'state', description: ' TODO ', stateToString: LoanStateToString })),
    transitionState: yup
      .number()
      .required()
      .default(0)
      .max(7)
      .min(0)
      .label('Loan Transition Status')
      .meta(
        toFieldMetaData({
          propName: 'transitionState',
          description: ' TODO ',
          stateToString: LoanTransitionStateToString,
        })
      ),
    durationDays: yup
      .number()
      .required()
      .label('Loan Duration')
      .meta(
        toFieldMetaData({
          propName: 'durationDays',
          description: ' The duration in days of the loan; also known as loan tenor. ',
          stringType: StringTypeEnum.TIME_INTERVAL_DAYS,
        })
      ),
    principal: yupErc20Wei
      .required()
      .default('0')
      .label('Principal Amount')
      .meta(
        toFieldMetaData({
          propName: 'principal',
          description: ' The current principal for the loan in Wei ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    startingPrincipal: yupErc20Wei
      .required()
      .default('0')
      .label('Starting Principal Amount')
      .meta(
        toFieldMetaData({
          propName: 'startingPrincipal',
          description: ' The principal for the loan at commencement in Wei ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    interest: yupErc20Wei
      .required()
      .default('0')
      .label('Interest of Loan')
      .meta(toFieldMetaData({ propName: 'interest', description: ' TODO ', stringType: StringTypeEnum.STABLE_COINS })),
    indicativeInterest: yupErc20Wei
      .required()
      .default('0')
      .label('Indicative Interest of Loan')
      .meta(
        toFieldMetaData({
          propName: 'indicativeInterest',
          description: ' The amount of interest in USDC-1e6 expected to accrue if held to maturity ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    originationFee: yupErc20Wei
      .required()
      .default('0')
      .label('Origination Fee Amount')
      .meta(
        toFieldMetaData({
          propName: 'originationFee',
          description: ' The sum of the Platform Fee and Advisor Fee in basis points ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    indicativeApr: yupApr
      .required()
      .default('0')
      .label('Indicative APR')
      .meta(
        toFieldMetaData({
          propName: 'indicativeApr',
          description: ' The indicative loan fee rate in basis points ',
          stringType: StringTypeEnum.APR,
        })
      ),
    finalizedApr: yupApr
      .required()
      .default('0')
      .label('Finalized APR')
      .meta(
        toFieldMetaData({
          propName: 'finalizedApr',
          description: ' The confirmed loan fee rate in basis points ',
          stringType: StringTypeEnum.APR,
        })
      ),

    exchangeRateAtDeposit: yupErc20Wei
      .required()
      .default('0')
      .label('exchangeRateAtDeposit')
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateAtDeposit',
          description: ' The exchange rate in Wei of Vault Tokens to USDC at loan commencement 1e18 is 1:1 ration ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    exchangeRateAtMaturity: yupErc20Wei
      .required()
      .default('0')
      .label('Exchange Rate At Maturity')
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateAtMaturity',
          description: ' The exchange rate in Wei of Vault Tokens to USDC at loan maturity 1e18 is 1:1 ration ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    fundingVaultBalance: yupErc20Wei.label('Borrower Vault Balance').meta(
      toFieldMetaData({
        propName: 'fundingVaultBalance',
        description: ' The USDC-1e6 balance of the funding vault ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

    assetsRolloverToNextLoan: yupErc20Wei
      .required()
      .default('0')
      .label('Assets Rollover To Next Loan')
      .meta(
        toFieldMetaData({
          propName: 'assetsRolloverToNextLoan',
          description: ' The amount in USDC-1e6 that is rolling over into the next loan cycle ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    assetsToReturnToPool: yupErc20Wei
      .required()
      .default('0')
      .label('Assets To Return To Pool')
      .meta(
        toFieldMetaData({
          propName: 'assetsToReturnToPool',
          description: ' The amount in USDC-1e6 that needs repaid into the Loan Vault to meet withdrawals ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    accrualStartDayTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('Accrual Start Day Timestamp')
      .meta(
        toFieldMetaData({
          propName: 'accrualStartDayTimestamp',
          description: ' The Epoch Unix Timestamp in seconds for the Loan Commencement Date ',
          stringType: StringTypeEnum.ETH_DATE,
        })
      ),
    accrualStartTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('Accrual Start Timestamp')
      .meta(
        toFieldMetaData({
          propName: 'accrualStartTimestamp',
          description: ' The Epoch Unix Timestamp in seconds for the Loan Commencement Date ',
          stringType: StringTypeEnum.ETH_TIME,
        })
      ),

    transferInWindowDurationDays: yup
      .number()
      .required()
      .default(0)
      .label('Transfer In Window Duration')
      .meta(
        toFieldMetaData({
          propName: 'transferInWindowDurationDays',
          description: ' The duration of the Transfer In Window in number of days ',
          stringType: StringTypeEnum.TIME_INTERVAL_DAYS,
        })
      ),
    transferOutWindowDurationDays: yup
      .number()
      .required()
      .default(0)
      .label('Transfer Out Window Duration')
      .meta(
        toFieldMetaData({
          propName: 'transferOutWindowDurationDays',
          description: ' The duration of the Transfer Out Window in number of days ',
          stringType: StringTypeEnum.TIME_INTERVAL_DAYS,
        })
      ),

    depositClosingTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('depositClosingTimestamp')
      .meta(
        toFieldMetaData({
          propName: 'depositClosingTimestamp',
          description:
            ' The Epoch Unix Timestamp in seconds for the close of the Investment Window for the loan cycle ',
          stringType: StringTypeEnum.ETH_TIME,
        })
      ),
    redemptionRequestClosingTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('redemptionRequestClosingTimestamp')
      .meta(
        toFieldMetaData({
          propName: 'redemptionRequestClosingTimestamp',
          description: ' TODO ',
          stringType: StringTypeEnum.ETH_TIME,
        })
      ),
    maturingTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('maturingTimestamp')
      .meta(
        toFieldMetaData({
          propName: 'maturingTimestamp',
          description: ' The Epoch Unix Timestamp in seconds for the maturity date of the loan ',
          stringType: StringTypeEnum.ETH_TIME,
        })
      ),
    redemptionAvailableTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('redemptionAvailableTimestamp')
      .meta(
        toFieldMetaData({
          propName: 'redemptionAvailableTimestamp',
          description: ' TThe Epoch Unix Timestamp in seconds for when vault tokens can be redeemed for USDC ',
          stringType: StringTypeEnum.ETH_TIME,
        })
      ),
    canRequestRedemption: yup
      .boolean()
      .required()
      .default(false)
      .label('canRequestRedemption')
      .meta(
        toFieldMetaData({
          description:
            ' If True, withdrawals can be requested; If False, withdrawals cannot be requested. Only False in the two days before maturity. ',
          propName: 'canRequestRedemption',
        })
      ),

    totalSupply: yupErc20Wei
      .required()
      .default('0')
      .label('Total Supply')
      .meta(toFieldMetaData({ propName: 'totalSupply', description: ' TODO ', stringType: StringTypeEnum.PPT_SHARES })),

    assetsFromPool: yupErc20Wei
      .required()
      .default('0')
      .label('assetsFromPool')
      .meta(
        toFieldMetaData({ propName: 'assetsFromPool', description: ' TODO ', stringType: StringTypeEnum.STABLE_COINS })
      ),
    assetToReturnToPool: yupErc20Wei
      .required()
      .default('0')
      .label('assetToReturnToPool')
      .meta(
        toFieldMetaData({
          propName: 'assetToReturnToPool',
          description: ' TODO ',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    assetsFromPriorLoan: yupErc20Wei
      .required()
      .default('0')
      .label('assetsFromPriorLoan')
      .meta(
        toFieldMetaData({
          propName: 'assetsFromPriorLoan',
          description: ' TODO ',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
  })

  .label('LoanCTO')
  .meta(toFieldMetaData({ propName: 'LoanCTO', description: 'LoanCTO' }))

export interface LoanCTO extends Asserts<typeof LoanCTOSchema> {
  liquidityAssetAddr?: EthAddr
  symbol?: string
  borrowerAddr: EthAddr
  loanAddr: EthAddr
  state: LoanLifeCycleState
  poolAddr: EthAddr
  interest: ERC20Wei
  indicativeInterest: ERC20Wei
  originationFee: ERC20Wei
  principal: ERC20Wei
  startingPrincipal: ERC20Wei
  indicativeApr: APR
  finalizedApr: APR
  exchangeRateAtDeposit: ERC20Wei
  exchangeRateAtMaturity: ERC20Wei
  fundingVaultBalance: ERC20Wei
  assetsRolloverToNextLoan: ERC20Wei
  assetsToReturnToPool: ERC20Wei
  assetsFromPriorLoan: ERC20Wei
  assetsFromPool: ERC20Wei
  totalSupply: ERC20Wei
}
